import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SignInButton, SignUpButton } from "@clerk/clerk-react";
import { SiteNav } from "./SiteNav";
import { SiteFooter } from "./SiteFooter";
import { BlockList } from "./BlockRenderer";
import { PlayButton } from "./PlayButton";
import { PromoCarousel } from "./PromoCarousel";
import { Logo } from "../Logo";
import { authEnabled } from "../AuthGate";
import { getPageBlocks, type Block } from "../../sim/cms";
import { agoText, useGardenSummary, useWho } from "./visitor";
import { ClubBand, ClubTopButton } from "./GardenClub";
import { FISH } from "../../sim/fishing";
import { INSECTS } from "../../sim/insects";
import { AWAY_CAP_HOURS } from "../../sim/plantGrowth";
import { track } from "../../sim/analytics";
import { getAppConfig } from "../../sim/env";

/**
 * THE HOME PAGE — the front door to the game, written for WHO IS ARRIVING. Its picture is a composed scene
 * (sim/showcase variant 11) of EARLY plants only, so the front door never spoils the late or fantasy blooms:
 *
 *   • a FIRST-TIME visitor sees what the game is (a real in-game night garden behind the hero, four feature
 *     cards made from real screenshots, a three-step "how to start", a short FAQ) and one large PLAY button;
 *   • a RETURNING gardener is greeted by name with their garden at a glance — level, coins, plants growing,
 *     when they last played — and the same button reads "Continue your garden";
 *   • a signed-out player who has played on this device is recognised from the local save and asked to sign in
 *     to pick up where they left off.
 * On the live site playing needs an account (AuthGate PlayRoute), so the copy never promises otherwise.
 *
 * THE PLAY BUTTON IS THE PAGE. It is the largest thing on it, it is in the first screen at every width, Enter
 * presses it from anywhere on the page, and a "Play fullscreen" option sits beside it. (PlayButton still owns
 * the maintenance state and the admin Live/Test split.)
 *
 * THE BANNER IS OPTIONAL. News and seasonal events go in the rotating PromoCarousel (Admin → Page Builder →
 * "Landing banner"); with no slides it renders nothing and the page is complete without it. Page Builder blocks
 * on "landing" now render as an extra section under the built-in page instead of REPLACING it, so a CMS edit
 * can add to the front door without being able to take the Play button off it.
 */

export function Landing() {
  const [blocks, setBlocks] = useState<Block[] | null>(null);
  useEffect(() => {
    let alive = true;
    getPageBlocks("landing").then((b) => { if (alive) setBlocks(b); });
    return () => { alive = false; };
  }, []);

  return (
    <div className="site lp">
      <SiteNav />
      <TopButtons />
      <Hero />
      <div className="lp-wrap">
        <PromoCarousel />
        <ClubBand />
        <Features />
        <GettingStarted />
        <Faq />
        {blocks && blocks.length > 0 && <div className="cms-page lp-cms"><BlockList blocks={blocks} /></div>}
      </div>
      <SiteFooter />
    </div>
  );
}

function Hero() {
  const navigate = useNavigate();
  const who = useWho();
  const { garden, loading } = useGardenSummary(who.signedIn);
  const returning = !!garden;
  const needsSignIn = authEnabled && !who.signedIn;

  // ENTER PLAYS, from anywhere on the page that is not a field or a control of its own.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || e.repeat || e.altKey || e.ctrlKey || e.metaKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.closest("input, textarea, select, button, a, [contenteditable]"))) return;
      const play = document.querySelector<HTMLAnchorElement>(".lp-hero a.lp-play");
      if (play) { e.preventDefault(); play.click(); }   // a maintenance-closed Play is not a link, so nothing happens
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const playFullscreen = () => {
    const el = document.documentElement;
    const go = () => navigate("/play");
    track("cta_click", { id: "hero.play_fullscreen" });
    if (el.requestFullscreen) el.requestFullscreen().then(go, go); else go();
  };

  const title = returning
    ? `Welcome back${who.name ? `, ${who.name}` : ""}!`
    : who.signedIn ? `Welcome${who.name ? `, ${who.name}` : ""}!` : null;
  const playLabel = returning ? (needsSignIn ? "Sign in and continue" : "Continue your garden")
    : who.signedIn ? "Start your garden" : "Play free";

  return (
    <header className="lp-hero">
      <div className="lp-hero-bg" aria-hidden="true" />
      <div className="lp-hero-inner">
        <h1 className="lp-logo"><Logo height={108} alt="Shelf Garden" tone="light" /></h1>
        {title
          ? <p className="lp-hello">{title}</p>
          : <p className="lp-tag">Grow flowers in your greenhouse, dig a pond, and collect the fish and insects your plants attract. It runs in your browser, and you can play at your own pace.</p>}

        {returning && garden && (
          <div className="lp-garden" aria-label="Your garden">
            <span>🪙 <b>{garden.coins.toLocaleString()}</b></span>
            <span>🌱 <b>{garden.growing}</b> growing</span>
            {garden.savedAt > 0 && <span className="lp-dim">last played {agoText(garden.savedAt)}</span>}
          </div>
        )}
        {loading && !returning && <div className="lp-garden lp-garden-loading" aria-hidden="true">Checking your garden…</div>}

        <div className="lp-cta">
          <PlayButton label={`▶ ${playLabel}`} className="site-btn primary lp-play" />
          {/* iPhone Safari has no Fullscreen API: there the button would only do what Play does */}
          {!!document.documentElement.requestFullscreen && (
            <button type="button" className="lp-fullscreen" onClick={playFullscreen} title="Open the game fullscreen">⛶ Play fullscreen</button>
          )}
        </div>
        <div className="lp-cta-note">
          {needsSignIn && !returning && (
            <>
              <span>It's free and takes a few seconds. Your garden saves automatically.</span>
              <span className="lp-auth-row">
                <SignUpButton mode="modal" forceRedirectUrl="/play"><button type="button" className="lp-link">Create a free account</button></SignUpButton>
                <span className="lp-dim">·</span>
                <SignInButton mode="modal" forceRedirectUrl="/play" signUpForceRedirectUrl="/play"><button type="button" className="lp-link">Already have an account? Sign in</button></SignInButton>
              </span>
            </>
          )}
          {needsSignIn && returning && <span>Sign in to pick up where you left off.</span>}
          <span className="lp-kbd-hint">or press <kbd>Enter</kbd></span>
        </div>

        <ul className="lp-pills" aria-label="At a glance">
          <li>Free to play</li><li>No download</li><li>Saves automatically</li><li>Keeps growing while you're away</li>
        </ul>
      </div>
      <a className="lp-scroll" href="#lp-features" aria-label="See what you can do">
        <svg width="26" height="16" viewBox="0 0 26 16" aria-hidden="true"><path d="M3 4l10 9 10-9" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </a>
    </header>
  );
}

const FEATURES = [
  { img: "/site/feat-grow.webp", title: "Grow a greenhouse", text: "Fill racks of pots with over a hundred flowers, succulents and fantasy blooms. Set grow lights to each plant's favorite color and program sprinklers from the terminal." },
  { img: "/site/feat-decorate.webp", title: "Design your garden", text: "Dig ponds and streams, lay paths and fences, and dress it all with lanterns, fountains and trees that sway in the weather." },
  { img: "/site/feat-fishing.webp", title: "Fish the water", text: `${FISH.length} fish to find. Some only bite at dawn, in fog, or in a pond big enough to hold them.` },
  { img: "/site/feat-garden.webp", title: "Collect the wildlife", text: `${INSECTS.length} insects visit the plants you grow. Net them for your cabinet and earn their badges as your collection grows.` },
];

/** While Kickstarter mode is on, the cards above describe the FULL game (grow lights, sprinklers, the whole roster)
 *  but the demo stops at the level cap — one small line under the heading says so (sim/kickstarter.ts). */
function Features() {
  const [demoCap, setDemoCap] = useState<number | null>(null);
  useEffect(() => {
    let alive = true;
    void getAppConfig().then((c) => { if (alive && c.kickstarter.on) setDemoCap(c.kickstarter.levelCap); });
    return () => { alive = false; };
  }, []);
  return (
    <section className="lp-section" id="lp-features" aria-labelledby="lp-feat-h">
      <h2 id="lp-feat-h" className="lp-h2">What's in the game</h2>
      {demoCap !== null && (
        <p className="lp-demo-note" data-testid="lp-demo-note">The free demo goes up to level {demoCap}. Grow lights, sprinklers and everything else arrive at full release.</p>
      )}
      <div className="lp-features">
        {FEATURES.map((f) => (
          <article key={f.title} className="lp-feature">
            <img src={f.img} alt="" loading="lazy" />
            <div className="lp-feature-copy">
              <h3>{f.title}</h3>
              <p>{f.text}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function GettingStarted() {
  const steps = authEnabled
    ? [
        { n: 1, t: "Press Play", s: "Create a free account in a few seconds. Your garden is saved to the cloud and follows you to any device." },
        { n: 2, t: "Plant your first seeds", s: "Pick a seed, tap or click a pot, and watch it grow in real time. Harvest, sell, and level up to unlock more." },
        { n: 3, t: "Decorate it", s: "Spend your earnings on decor, gear and new plants. Your garden keeps growing while you're away." },
      ]
    : [
        { n: 1, t: "Press Play", s: "There's no download. The game opens right here in your browser." },
        { n: 2, t: "Plant your first seeds", s: "Pick a seed, tap or click a pot, and watch it grow in real time. Harvest, sell, and level up to unlock more." },
        { n: 3, t: "Decorate it", s: "Spend your earnings on decor, gear and new plants. Your garden keeps growing while you're away." },
      ];
  return (
    <section className="lp-section" aria-labelledby="lp-start-h">
      <h2 id="lp-start-h" className="lp-h2">Getting started</h2>
      <ol className="lp-steps">
        {steps.map((s) => (
          <li key={s.n}><span className="lp-step-n" aria-hidden="true">{s.n}</span><div><b>{s.t}</b><p>{s.s}</p></div></li>
        ))}
      </ol>
    </section>
  );
}

/**
 * THE TOP BUTTONS — What's New, Shop, Profile and Garden Club as a row of large, lit buttons right under the
 * nav, ABOVE the hero, so the places a player goes besides the game are the first thing on the page after the
 * logo. The club's button reads "Join" to a visitor and "You're a member" to a member (site/GardenClub.tsx).
 */
function TopButtons() {
  return (
    <nav className="lp-topbar" aria-label="Main pages">
      <Link to="/whats-new" className="lp-topbtn news"><span><b>What's New</b><small>Updates &amp; events</small></span></Link>
      <Link to="/shop" className="lp-topbtn shop"><span><b>Shop</b><small>Rare plants &amp; treasures</small></span></Link>
      <Link to="/profile" className="lp-topbtn profile"><span><b>Profile</b><small>Badges &amp; account</small></span></Link>
      <ClubTopButton />
    </nav>
  );
}

function Faq() {
  const items: { q: string; a: string }[] = [
    { q: "Is Shelf Garden free?", a: "Yes, the game is free to play in your browser. The Shop sells optional extras like rare plants and garden treasures, but you don't need any of them to level up or catch every fish and insect." },
    authEnabled
      ? { q: "Do I need an account?", a: "Yes, a free one. That's how your garden gets saved, so you can close the tab, switch computers and carry on where you left off." }
      : { q: "Where is my garden saved?", a: "Right here in this browser, automatically, every few seconds." },
    { q: "Does my garden grow while I'm away?", a: `Yes. Plants keep growing, soil keeps drying and machines keep running for up to ${AWAY_CAP_HOURS} hours after you leave.` },
    { q: "What do I need to play?", a: "Any modern browser, on a computer, tablet or phone. A mouse is best, but touch works." },
  ];
  return (
    <section className="lp-section" aria-labelledby="lp-faq-h">
      <h2 id="lp-faq-h" className="lp-h2">Questions</h2>
      <div className="lp-faq">
        {items.map((it) => (
          <details key={it.q}><summary>{it.q}</summary><p>{it.a}</p></details>
        ))}
      </div>
    </section>
  );
}
